import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";
import { Icon } from "../Icon";
import { Toast } from "../Toast/Toast";
import { usePassions } from "../../hooks/usePassions";
import { useAcid } from "../../hooks/useAcid";
import { PASSION_DEFINITIONS } from "../../hooks/usePassionsData";
import { usePassionsStore } from "../../store";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import { useShallow } from "zustand/react/shallow";
import styles from "./CollectionsModule.module.css";

const PASSIONS_LIST = [
  "golf",
  "ski",
  "safari",
  "music",
  "culinary",
  "paddle",
  "bike",
  "surf",
  "fish",
  "dive",
  "culture",
  "beach",
  "spa",
  "hike",
];

export const CollectionsModule = ({ onSaveSuccess }) => {
  const { acid } = useAcid();
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, success
  const [hasChanges, setHasChanges] = useState(false);
  const [toast, setToast] = useState(null);

  // Get the store functions for reactive UI updates
  const setSelectedPassions = usePassionsStore(
    (state) => state.setSelectedPassions,
  );
  const storeSelectedPassions = usePassionsStore(
    useShallow((state) => state.selectedPassions),
  );

  // Use 'collections-module' as the experience type for session storage
  const { tapPassion, savePassionsToServer, getSelectedPassionsAsFlatArray } =
    usePassions("collections-module");

  // Initialize store from session storage on mount
  useEffect(() => {
    const savedPassions = getSelectedPassionsAsFlatArray();
    if (savedPassions && savedPassions.length > 0) {
      console.log(
        "[CollectionsModule] Initializing store from session storage:",
        savedPassions,
      );
      setSelectedPassions(savedPassions);
    }
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Check if passion is selected - use store as source of truth for UI
  const isPassionSelected = (passionId) => {
    return storeSelectedPassions.includes(passionId);
  };

  const isAnyPassionSelected = () => {
    return storeSelectedPassions.length > 0;
  };

  const handleToggle = (passionId) => {
    setSaveStatus("idle");
    setHasChanges(true);

    // Toggle in session storage (for persistence)
    tapPassion(passionId, 1);

    // Calculate new passions list based on current store state
    const isCurrentlySelected = storeSelectedPassions.includes(passionId);

    const newPassions = isCurrentlySelected
      ? storeSelectedPassions.filter((p) => p !== passionId)
      : [...storeSelectedPassions, passionId];

    console.log(
      "[CollectionsModule] Toggling passion:",
      passionId,
      "isCurrentlySelected:",
      isCurrentlySelected,
      "newPassions:",
      newPassions,
    );

    // Update store with ALL selected passions (not just toggle)
    setSelectedPassions(newPassions);
  };

  const handleSave = async () => {
    if (!acid) {
      console.warn("No ACID, cannot save.");
      // In a real scenario, we might trigger login here.
      return;
    }

    setSaveStatus("saving");
    try {
      await savePassionsToServer(acid);
      setSaveStatus("success");
      setHasChanges(false);
      setToast({ message: "Preferences saved successfully!", type: "success" });
      if (onSaveSuccess) {
        setTimeout(() => {
          onSaveSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error("Save failed", error);
      setSaveStatus("idle");
      setToast({ message: "Failed to save preferences.", type: "error" });
    }
  };

  return (
    <div className={styles.container}>
      {toast && (
        <div className={styles.toastWrapper}>
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </div>
      )}
      <h2 className={styles.title}>Designed around what moves you</h2>
      <p className={styles.subtitle}>
        Tell us your passions to unlock experiences, destinations, and stays
        made for you.
      </p>
      <div className={styles.instruction}>
        <Icon name="like" size={16} />
        Tap to like
      </div>

      <motion.div className={styles.grid} layout>
        {PASSIONS_LIST.map((passionId) => {
          const isSelected = isPassionSelected(passionId);
          const definition = PASSION_DEFINITIONS[passionId];
          return (
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              key={passionId}
              className={classNames(
                styles.bubble,
                {
                  [styles.bubbleSelected]: isSelected,
                },
                "custom_click_track",
              )}
              onClick={() => handleToggle(passionId)}
              {...getAnalyticsClickTrackingAttributes(
                "Collections Module",
                "click",
                `${isSelected ? "Deselected" : "Selected"} ${passionId}`,
              )}
            >
              <div className={styles.iconWrapper}>
                <Icon
                  name={definition.icon}
                  size={24}
                  className={styles.bubbleIcon}
                />
              </div>
              <span className={styles.bubbleLabel}>{definition.label}</span>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Only show save button when user is logged in */}
      {acid && (
        <button
          className={classNames(
            styles.button,
            {
              [styles.buttonSuccess]: saveStatus === "success",
            },
            "custom_click_track",
          )}
          onClick={handleSave}
          {...getAnalyticsClickTrackingAttributes(
            "Collections Module",
            "click",
            "Clicked Curate my travels",
          )}
          disabled={
            saveStatus === "saving" ||
            saveStatus === "success" ||
            !isAnyPassionSelected() ||
            !hasChanges
          }
        >
          {saveStatus === "saving"
            ? "Saving..."
            : saveStatus === "success"
              ? "Save Success"
              : "Curate my travels"}
        </button>
      )}
    </div>
  );
};
